import { scaleLog, scaleSymlog, type ScaleSymLog } from 'd3-scale';
import { uniq } from 'lodash-es';

export const getSymlogScale = (): ScaleSymLog<number, number> => {
  const scale = scaleSymlog();

  scale.nice = function () {
    const originalDomain = this.domain();
    const orderedDomain = getOrderedDomain(originalDomain);
    const [min, max] = orderedDomain;

    if (isPositive(orderedDomain) || isNegative(orderedDomain)) {
      const niceDomain = getNiceDomain(originalDomain);
      return this.domain(niceDomain);
    }

    const [niceMin] = getNiceDomain([min, 0]);
    const [, niceMax] = getNiceDomain([0, max]);

    if (isReversedDomain(originalDomain)) {
      return this.domain([niceMax, niceMin]);
    }

    return this.domain([niceMin, niceMax]);
  };

  scale.ticks = function (count: number = 10) {
    const originalDomain = this.domain();
    const orderedDomain = getOrderedDomain(originalDomain);
    const [min, max] = orderedDomain;

    if (min === 0 && max === 0) {
      return [0];
    }

    if (isPositive(orderedDomain) || isNegative(orderedDomain)) {
      const logScale = scaleLog().domain(originalDomain);
      return logScale.ticks(count);
    }

    let ticks: number[];

    if (isNonPositive(orderedDomain)) {
      const negativeTicks = getTicks(min, count - 1);
      ticks = [...negativeTicks, 0];
    } else if (isNonNegative(orderedDomain)) {
      const positiveTicks = getTicks(max, count - 1);
      ticks = [0, ...positiveTicks];
    } else {
      const halfCount = Math.floor(count / 2);
      const negativeTicks = getTicks(min, halfCount);
      const positiveTicks = getTicks(max, halfCount);
      ticks = [...negativeTicks, 0, ...positiveTicks];
    }

    ticks = uniq(ticks);

    if (isReversedDomain(originalDomain)) {
      return ticks.reverse();
    }

    return ticks;
  };

  scale.tickFormat = function (count: number = 10, specifier?: string) {
    const originalDomain = this.domain();
    const orderedDomain = getOrderedDomain(originalDomain);
    const [min, max] = orderedDomain;

    if (isPositive(orderedDomain) || isNegative(orderedDomain)) {
      const logScale = scaleLog().domain(originalDomain);
      return logScale.tickFormat(count);
    }

    if (isNonPositive(orderedDomain)) {
      const negLogScale = scaleLog().domain([min, -1]);
      return (d) => {
        return d.valueOf() === 0
          ? '0'
          : negLogScale.tickFormat(count - 1, specifier)(d);
      };
    }

    if (isNonNegative(orderedDomain)) {
      const posLogScale = scaleLog().domain([1, max]);
      return (d) => {
        return d.valueOf() === 0
          ? '0'
          : posLogScale.tickFormat(count - 1, specifier)(d);
      };
    }

    const negLogScale = scaleLog().domain([min - 1, -1]);
    const posLogScale = scaleLog().domain([1, max + 1]);
    const halfCount = Math.floor(count / 2);

    return (d) => {
      if (d.valueOf() < 0) {
        return negLogScale.tickFormat(halfCount, specifier)(d);
      }
      if (d.valueOf() > 0) {
        return posLogScale.tickFormat(halfCount, specifier)(d);
      }
      return '0';
    };
  };

  return scale;
};

const isPositive = ([min]: number[]) => min > 0;
const isNonNegative = ([min]: number[]) => min >= 0;
const isNegative = ([, max]: number[]) => max < 0;
const isNonPositive = ([, max]: number[]) => max <= 0;

const isReversedDomain = ([min, max]: number[]) => max < min;

const getOrderedDomain = (domain: number[]) => {
  const [min, max] = domain;
  return isReversedDomain(domain) ? [max, min] : domain;
};

const getNiceDomain = (domain: number[]) =>
  scaleLog().domain(domain).nice().domain();

const getTicks = (boundary: number, count: number) => {
  if (boundary === 0) {
    throw new Error('Unexpected boundary');
  }

  if (Math.abs(boundary) > 1) {
    const domain = boundary > 0 ? [1, boundary] : [boundary, -1];
    return scaleLog().domain(domain).ticks(count);
  }

  const delta = Math.sign(boundary);
  const domain =
    boundary > 0 ? [delta, boundary + delta] : [boundary + delta, delta];

  return scaleLog()
    .domain(domain)
    .ticks(count)
    .map((tick) => +(tick - delta).toFixed(4));
};
